import { useState } from 'react';
import FileUploader from 'devextreme-react/file-uploader';
import { ALLOWED_FILE_EXTENSIONS, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from '../../constants/fileUploadLimits';
import { DropPanel } from './components/DropPanel';
import { RejectedFiles } from './components/RejectedFiles';
import { PickedFilesTable } from './components/PickedFilesTable';
import './styles/_fileDropZone.scss';

interface FileDropZoneProps {
    maxFiles: number;
    /** Called with the usable picked files: too-large ones dropped, and no more than maxFiles. */
    onChange: (files: File[]) => void;
}

/**
 * Picks files with "Select Files", or by dropping them on the panel, and lists the ones picked so they can be removed.
 * Nothing is uploaded here. The panel and list are our own; a hidden FileUploader opens the file dialog and takes the
 * drops (its dialogTrigger and dropZone).
 */
export const FileDropZone = ({ maxFiles, onChange }: FileDropZoneProps) => {
    const [pickedFiles, set_pickedFiles] = useState<File[]>([]);
    // Why files from the last pick were left out (wrong type, too large, too many)
    const [rejections, set_rejections] = useState<string[]>([]);
    const [isDraggingOver, set_isDraggingOver] = useState(false);
    // FileUploader needs the elements themselves, so they're held as state once rendered
    const [dropZone, set_dropZone] = useState<HTMLDivElement | null>(null);
    const [selectButton, set_selectButton] = useState<HTMLDivElement | null>(null);

    const updatePicked = (files: File[]) => {
        set_pickedFiles(files);
        onChange(files);
    };

    const addFiles = (files: File[]) => {
        set_isDraggingOver(false);
        // With a single file allowed, a new pick replaces it
        const next = maxFiles === 1 ? [] : [...pickedFiles];
        const reasons: string[] = [];
        files.forEach((file) => {
            const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
            if (!ALLOWED_FILE_EXTENSIONS.includes(extension)) {
                reasons.push(`${file.name}: this file type isn't allowed`);
            } else if (file.size > MAX_FILE_SIZE_BYTES) {
                reasons.push(`${file.name}: larger than ${MAX_FILE_SIZE_MB} MB`);
            } else if (next.some((picked) => picked.name === file.name && picked.size === file.size)) {
                // Already picked
            } else if (next.length >= maxFiles) {
                reasons.push(`${file.name}: no more than ${maxFiles} files can be added at once`);
            } else {
                next.push(file);
            }
        });
        set_rejections(reasons);
        updatePicked(next);
    };

    return (
        <div className='fileDropZone'>
            <DropPanel
                dropZoneRef={set_dropZone}
                selectButtonRef={set_selectButton}
                isDraggingOver={isDraggingOver}
            />
            <RejectedFiles reasons={rejections} />
            <PickedFilesTable
                files={pickedFiles}
                onRemove={(file) => updatePicked(pickedFiles.filter((picked) => picked !== file))}
            />
            {dropZone && selectButton && (
                <FileUploader
                    visible={false}
                    // Not useForm: in that mode FileUploader leaves drops to its own (hidden) file input, so a drop on
                    // the panel would just open the file. Nothing uploads here; that's only from its hidden Upload buttons
                    uploadMode='useButtons'
                    // Each pick reports only the new files (addFiles keeps the list), so a removed file doesn't come back.
                    // extendSelection is a FileUploader option (default true) its typings leave out, hence the spread
                    {...{ extendSelection: false }}
                    multiple={maxFiles > 1}
                    accept={ALLOWED_FILE_EXTENSIONS.join(',')}
                    dialogTrigger={selectButton}
                    dropZone={dropZone}
                    onDropZoneEnter={() => set_isDraggingOver(true)}
                    onDropZoneLeave={() => set_isDraggingOver(false)}
                    onValueChange={addFiles}
                />
            )}
        </div>
    );
};
